import * as React from 'react'
import { supabase } from './supabase'
import { AdminField, Button } from './ui'
import { productGroupLabel } from './ProductSummary'

type ProductType = {
  product_group: string
  assignment_mode: 'auto' | 'manual'
  manual_quantity_limit: number
}

const errorMessage = (error: unknown) => error && typeof error === 'object' && 'message' in error
  ? String(error.message)
  : 'Assignment settings could not be loaded or saved.'

export function ProductAssignmentSettings({ canWrite }: { canWrite: boolean }) {
  const [types, setTypes] = React.useState<ProductType[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    let live = true
    async function load() {
      try {
        const { data, error } = await supabase.from('product_types').select('*').order('product_group')
        if (error) throw error
        if (live) setTypes(data ?? [])
      } catch (error) {
        if (live) setError(errorMessage(error))
      } finally {
        if (live) setLoading(false)
      }
    }
    void load()
    return () => { live = false }
  }, [])

  return <section className="panel settings-editors">
    <div className="panel-heading"><h2>Equipment assignment by product type</h2></div>
    <p>Manual assignment skips equipment stock checks and adds no turnaround buffer. Paid bookings await assignment at the top of Bookings. Date rules still apply. Changes apply to new checkouts.</p>
    {loading && <p>Loading assignment settings…</p>}
    {error && <p className="message error" role="alert">{error}</p>}
    <div className="card-grid">
      {types.map(type => <AssignmentSetting key={type.product_group} type={type} canWrite={canWrite} />)}
    </div>
  </section>
}

function AssignmentSetting({ type, canWrite }: { type: ProductType; canWrite: boolean }) {
  const [mode, setMode] = React.useState(type.assignment_mode)
  const [limit, setLimit] = React.useState(String(type.manual_quantity_limit))
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [saved, setSaved] = React.useState(false)

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canWrite || busy) return
    setBusy(true)
    setError('')
    setSaved(false)
    try {
      const { error } = await supabase.rpc('admin_set_product_assignment', {
        p_product_group: type.product_group,
        p_assignment_mode: mode,
        p_manual_quantity_limit: Number(limit),
      })
      if (error) throw error
      setSaved(true)
    } catch (error) {
      setError(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return <form className="settings-editor" onSubmit={event => void save(event)}>
    <h3>{productGroupLabel(type.product_group)}</h3>
    <fieldset disabled={!canWrite || busy}>
      <AdminField>
        Assignment
        <select value={mode} onChange={event => { setMode(event.target.value as ProductType['assignment_mode']); setSaved(false) }}>
          <option value="auto">Automatic</option>
          <option value="manual">Manual</option>
        </select>
      </AdminField>
      {mode === 'manual' && <AdminField>
        Maximum quantity shown in booking
        <input type="number" min="1" max="100" required value={limit}
          onChange={event => { setLimit(event.target.value); setSaved(false) }} />
      </AdminField>}
      {canWrite && <Button className="primary">{busy ? 'Saving…' : 'Save assignment settings'}</Button>}
    </fieldset>
    {error && <p className="message error" role="alert">{error}</p>}
    {saved && <p role="status">Assignment settings saved.</p>}
  </form>
}
